import type { OrgDashboard } from "../types";

// The one source for the segmented-bar vocabulary — keys, labels, and colours —
// shared by the by-field/corpus TABLES in `auditDashboard.tsx` and the stacked
// TREND bars in `metricsTrend.tsx`. Two copies would drift, and a trend bar
// whose "Coder only" is a different amber from the table's directly above it
// reads as a different measure rather than the same one over time.

/** The four mutually-exclusive judgement outcomes, in bar order.
 *
 *  Bar and legend both render from this one table. Amber and coral have no
 *  Tailwind utility in the preset (only blue/navy/teal/gray do), so they resolve
 *  through the `--ch-*` design tokens inline — a `bg-coronis-amber-500` class
 *  would compile to nothing and paint an invisible segment. */
export const OUTCOME_SEGMENTS: {
  key: string;
  label: string;
  color: string;
  pick: (c: {
    both: number;
    agentOnly: number;
    coderOnly: number;
    neither: number;
  }) => number;
}[] = [
  {
    key: "both",
    label: "Both acceptable",
    color: "var(--ch-teal-500)",
    pick: (c) => c.both,
  },
  {
    key: "agent",
    label: "Agent only",
    color: "var(--ch-blue-600)",
    pick: (c) => c.agentOnly,
  },
  {
    key: "coder",
    label: "Coder only",
    color: "var(--ch-amber-500)",
    pick: (c) => c.coderOnly,
  },
  {
    key: "neither",
    label: "Neither",
    color: "var(--ch-coral-500)",
    pick: (c) => c.neither,
  },
];

/** Corpus agreement segments: agree / differ / not comparable.
 *
 *  Not-comparable gets its own segment and its own neutral colour rather than
 *  being left out of the bar. A field where most cases are unscorable and a
 *  field where most cases agree are different situations, and a bar that showed
 *  only the scored slice would draw them identically. */
export const CORPUS_SEGMENTS: {
  key: string;
  label: string;
  color: string;
  pick: (r: OrgDashboard["corpus"]["fields"][number]) => number;
}[] = [
  {
    key: "agree",
    label: "Agree",
    color: "var(--ch-teal-500)",
    pick: (r) => r.agree,
  },
  {
    key: "differ",
    label: "Differ",
    color: "var(--ch-coral-500)",
    pick: (r) => r.differ,
  },
  {
    // Amber, deliberately between the coral of a real disagreement and the grey
    // of an unscorable field: a coverage gap is neither. It is the one segment
    // that shrinks as the coding backfill lands, so it reads as a queue burning
    // down rather than as a model getting worse.
    //
    // `--ch-amber-300`, not the `-400` this used to name: there is no amber-400
    // step in the design system (100/300/500/700), and an undefined custom
    // property is not a colour — SVG fell back to BLACK for the bar fill and the
    // legend dot rendered transparent. Only steps that exist in
    // `@hub/config/design-system.css`.
    key: "one_sided",
    label: "One side only",
    color: "var(--ch-amber-300)",
    pick: (r) => r.oneSided,
  },
  {
    key: "not_comparable",
    label: "Not comparable",
    color: "var(--ch-gray-300)",
    pick: (r) => r.notComparable,
  },
];
